import React from 'react'
import dayjs from 'dayjs'
import Head from 'next/head'
import {
  Alert,
  Button,
  Descriptions,
  Icons,
  Input,
  List,
  Modal,
  notification,
  Result,
  Show,
  Table,
  Tag,
  TextField,
  Typography,
  useTable,
} from '@pankod/refine-antd'
import {
  IResourceComponentsProps,
  LogicalFilter,
  useCan,
  useCreate,
  useNavigation,
  useOne,
  useResource,
} from '@pankod/refine-core'

import Filter, { TFilterForm } from '../../../components/ContentFilter'
import { kapabilitasMapper } from '@components/DataTableParticipants/blocks/utils'
import { CertificateTemplate } from '@resources/lms-management/certificate-template/types'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import {
  TLMSProgram,
  TLMSProgramDetail,
} from '@resources/lms-management/program/types'
import { TCommonError, TCommonResponse } from 'src/interfaces/common'
import useTableWithMeta from '../../../hooks/useTableWithMeta'

const RESOURCE = 'reject-certificate-tte-completed'
const WARNING = (
  <>
    Membatalkan sertifikat berarti{' '}
    <strong>proses harus dimulai dari awal</strong>, termasuk persetujuan dan
    penandatanganan ulang oleh pihak atau pejabat terkait.
  </>
)

const AccessGuard: React.FC<{
  action: 'list' | 'show'
  children: React.ReactNode
}> = ({ action, children }) => {
  const { data, isLoading } = useCan({ resource: RESOURCE, action })

  if (isLoading) return null
  if (!data?.can) {
    return <Result status="403" title="Akses ditolak" />
  }
  return <>{children}</>
}

const ProgramList: React.FC<IResourceComponentsProps> = ({ options }) => {
  const { showUrl, push } = useNavigation()
  const { tableProps, searchFormProps, filters } = useTableWithMeta<
    TLMSProgram,
    TCommonError,
    TFilterForm<{ name?: string }>
  >({
    resource: 'programs',
    dataProviderName: 'lms',
    initialSorter: [{ field: 'endDate', order: 'desc' }],
    onSearch: ({ name }) => [{ field: 'name', operator: 'eq', value: name }],
  })

  return (
    <List title="Daftar Program" canCreate={false}>
      <Head>
        <title>LMS Admin | {options?.label}</title>
      </Head>
      <Filter
        formProps={searchFormProps}
        filters={filters as LogicalFilter[]}
        layout="vertical"
        size="small"
        items={[{ label: 'Nama Program', name: 'name' }]}
      />
      <Table {...tableProps} rowKey="id" scroll={{ x: 700 }}>
        <Table.Column<TLMSProgram> title="ID" dataIndex="id" />
        <Table.Column<TLMSProgram> title="Nama Program" dataIndex="name" />
        <Table.Column<TLMSProgram> title="Status" dataIndex="status" />
        <Table.Column<TLMSProgram>
          title="Periode"
          render={(_, record) =>
            `${dayjs(record.startDate).format('DD MMM YYYY')} — ${dayjs(record.endDate).format('DD MMM YYYY')}`
          }
        />
        <Table.Column<TLMSProgram>
          title="Aksi"
          render={(_, record) => (
            <Button onClick={() => push(showUrl(RESOURCE, record.id))}>
              Lihat Template Sertifikat
            </Button>
          )}
        />
      </Table>
    </List>
  )
}

const TemplateList: React.FC<IResourceComponentsProps> = ({ options }) => {
  const { id: programId } = useResource()
  const { data: programData } = useOne<TCommonResponse<TLMSProgramDetail>>({
    resource: 'programs',
    id: programId,
    dataProviderName: 'lms',
    queryOptions: { enabled: !!programId },
  })
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<CertificateTemplate | null>(null)
  const [confirmation, setConfirmation] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const { tableProps, tableQueryResult } = useTable<CertificateTemplate>({
    resource: `programs/${programId}/certificate-templates`,
    dataProviderName: 'lms',
    metaData: { queryKey: { isTteCompleted: true } },
    queryOptions: { enabled: !!programId },
  })
  const { mutateAsync: rejectCertificate } = useCreate()

  const closeDialog = () => {
    setSelectedTemplate(null)
    setConfirmation('')
  }

  const handleReject = async () => {
    if (
      !selectedTemplate ||
      isSubmitting ||
      confirmation.trim().toLowerCase() !== 'batalkan sertifikat'
    ) {
      return
    }
    const templateId = selectedTemplate.id
    setIsSubmitting(true)
    let succeeded = false
    try {
      await rejectCertificate({
        resource: `ops-tools/reject-certificate-tte-completed/${templateId}`,
        values: {},
        dataProviderName: 'lms',
        successNotification: false,
        errorNotification: false,
      })
      succeeded = true
      notification.success({ message: 'Sertifikat berhasil dibatalkan' })
    } catch (error) {
      const details = showErrorNotification(
        error,
        'Gagal membatalkan sertifikat',
      )
      notification.error({
        message: details.message,
        description: details.description,
      })
    } finally {
      const refreshed = await tableQueryResult.refetch()
      if (succeeded) {
        closeDialog()
      } else if (
        refreshed.isSuccess &&
        refreshed.data &&
        !refreshed.data.data.some((template) => template.id === templateId)
      ) {
        closeDialog()
        notification.info({
          message: 'Template tidak lagi tersedia untuk dibatalkan',
        })
      }
      setIsSubmitting(false)
    }
  }

  return (
    <Show title="Pembatalan Sertifikat TTE" headerButtons={<></>}>
      <Head>
        <title>LMS Admin | {options?.label}</title>
      </Head>
      <Descriptions
        bordered
        column={1}
        labelStyle={{ width: 150 }}
        size="small"
        className="mb-4"
      >
        <Descriptions.Item label="ID">{programId}</Descriptions.Item>
        <Descriptions.Item label="Nama Program">
          {programData?.data?.data?.name || '-'}
        </Descriptions.Item>
      </Descriptions>
      <Alert
        type="info"
        showIcon
        message="Berikut adalah daftar template sertifikat TTE yang telah selesai diterbitkan. Anda dapat membatalkan/reject sertifikat TTE untuk memulai proses dari awal."
        className="!mb-4"
      />
      <Table {...tableProps} rowKey="id" scroll={{ x: 900 }}>
        <Table.Column<CertificateTemplate>
          title="ID"
          dataIndex="id"
          render={(value) => <TextField value={value} />}
        />
        <Table.Column<CertificateTemplate>
          title="Kapabilitas"
          dataIndex="kapabilitas"
          render={(value) => <TextField value={kapabilitasMapper(value)} />}
        />
        <Table.Column<CertificateTemplate>
          title="Jenis Sertifikat"
          dataIndex="certificateType"
          render={(value) =>
            value === 'COMPLETION'
              ? 'Penyelesaian (Lulus)'
              : 'Partisipasi (Tidak Lulus)'
          }
        />
        <Table.Column<CertificateTemplate>
          title="Status"
          dataIndex="state"
          render={(value) => (
            <Tag color={value === 'TEMPLATE_PUBLISHED' ? 'success' : 'warning'}>
              {value === 'TEMPLATE_PUBLISHED' ? 'Published' : 'Draft'}
            </Tag>
          )}
        />
        <Table.Column<CertificateTemplate>
          title="Dokumen"
          dataIndex="hasSupportingDocument"
          render={(value) => (value ? 'Tersedia' : 'Kosong')}
        />
        <Table.Column<CertificateTemplate>
          title="Kategori"
          render={(_, record) => record.certificateCategory?.name || '—'}
        />
        <Table.Column<CertificateTemplate>
          title="Actions"
          render={(_, record) => (
            <Button
              icon={<Icons.MinusCircleFilled />}
              danger
              onClick={() => setSelectedTemplate(record)}
            >
              Batalkan Sertifikat
            </Button>
          )}
        />
      </Table>
      <Modal
        title={`Batalkan Sertifikat ${selectedTemplate?.id ?? ''}`}
        visible={!!selectedTemplate}
        destroyOnClose
        onCancel={() => {
          if (!isSubmitting) closeDialog()
        }}
        onOk={handleReject}
        okText="Batalkan Sertifikat"
        okButtonProps={{
          danger: true,
          disabled: confirmation.trim().toLowerCase() !== 'batalkan sertifikat',
          loading: isSubmitting,
        }}
        cancelText="Kembali"
        closable={!isSubmitting}
        maskClosable={!isSubmitting}
        cancelButtonProps={{ disabled: isSubmitting }}
      >
        <Alert
          type="warning"
          showIcon
          message="Batalkan Sertifikat TTE"
          description={WARNING}
        />
        <Typography.Paragraph className="mt-4">
          Ketik "<strong>batalkan sertifikat</strong>" untuk melanjutkan.
        </Typography.Paragraph>
        <Input
          aria-label="Ketik batalkan sertifikat"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          disabled={isSubmitting}
        />
      </Modal>
    </Show>
  )
}

export const RejectCertificateTteCompletedList: React.FC<
  IResourceComponentsProps
> = (props) => (
  <AccessGuard action="list">
    <ProgramList {...props} />
  </AccessGuard>
)

export const RejectCertificateTteCompletedShow: React.FC<
  IResourceComponentsProps
> = (props) => (
  <AccessGuard action="show">
    <TemplateList {...props} />
  </AccessGuard>
)
